import type { VisualPayload, CommunityDay } from "../../types";
import { isSafeUrl } from "../../lib/safeUrl";
import { TYPE_TEXT_COLORS } from "../../utils/typeColors";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

// Type → Tailwind text colour.  Mirrors the palette used by
// PokemonCard / LoreCard so a "Dragon-type" move on a CD card looks
// the same shade as a "Dragon" type pill elsewhere.  Kept local to
// the card to avoid a shared utility import for one map.
interface Props { payload: VisualPayload }

// CD start/end timestamps are a "floating time" — ScrapedDuck publishes
// them with a Z suffix, but the semantic intent is "this is the
// wall-clock time wherever the player is": Community Days run 2pm-5pm
// in every timezone, not 2pm UTC. LeekDuck shows them as "at 2:00 PM
// Local Time" for this reason. Passing `timeZone: "UTC"` to the
// formatter preserves the source wall-clock components instead of
// converting them through the viewer's local zone (e.g. 14:00Z →
// 10:00 AM Eastern, which would mislead the user about when to show
// up).
const _WALL_CLOCK_TZ: Intl.DateTimeFormatOptions = { timeZone: "UTC" };

function _fmtDate(iso?: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    ..._WALL_CLOCK_TZ,
  });
}

function _fmtTimeWindow(starts?: string, ends?: string | null): string | null {
  if (!starts) return null;
  const s = new Date(starts);
  if (isNaN(s.getTime())) return null;
  const sTime = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", ..._WALL_CLOCK_TZ });
  if (!ends) return sTime;
  const e = new Date(ends);
  if (isNaN(e.getTime())) return sTime;
  return `${sTime}–${e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", ..._WALL_CLOCK_TZ })}`;
}

function CDImage({ cd }: { cd: CommunityDay }) {
  // Prefer the featured Pokémon's sprite over the CD's banner — the
  // sprite reads cleaner in a small list-row slot than the CD banner
  // which is designed for a larger hero context.  Falls through to
  // banner when featured isn't published yet (date-only placeholders).
  const featuredImg = cd.featured_pokemon?.[0]?.image;
  const heroImg = featuredImg || cd.image;
  // Route through PokemonSprite so VITE_ENABLE_SPRITES applies
  // uniformly — toggling the kill switch hides the CD hero too,
  // matches every other card.
  return (
    <PokemonSprite
      src={heroImg ?? undefined}
      alt={cd.featured_pokemon?.[0]?.name ?? cd.name}
      size="lg"
    />
  );
}

function ShinySparkle() {
  return (
    <span
      aria-label="shiny available"
      title="Shiny available"
      className="text-yellow-300 text-[11px]"
    >
      ✦
    </span>
  );
}

function CommunityDayBlock({ cd }: { cd: CommunityDay }) {
  const dateLabel = _fmtDate(cd.starts_at);
  const timeLabel = _fmtTimeWindow(cd.starts_at, cd.ends_at);
  const featuredNames = (cd.featured_pokemon ?? [])
    .map((p) => p.name)
    .filter((n): n is string => Boolean(n));
  const shinyNames = (cd.shinies ?? [])
    .map((p) => p.name)
    .filter((n): n is string => Boolean(n));
  const isAnnounced = cd.is_announced;

  return (
    <article
      className={`rounded-lg border p-3 sm:p-4 ${
        isAnnounced
          ? "bg-white/[0.04] border-pink-500/25"
          : "bg-white/[0.02] border-white/[0.10] border-dashed"
      }`}
    >
      {/* Header: name + date + time */}
      <div className="flex items-start gap-3 mb-2">
        <CDImage cd={cd} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-tight break-words ${
            isAnnounced ? "text-white" : "text-white/65"
          }`}>
            {cd.name}
          </p>
          <p className="text-[11px] text-white/55 mt-0.5">
            {dateLabel}
            {timeLabel && (
              <>
                {" · "}
                <span className="tabular-nums">{timeLabel}</span>
              </>
            )}
          </p>
          {!isAnnounced && (
            <p className="text-[11px] text-white/45 italic mt-1">
              Date scheduled · featured Pokémon not yet announced
            </p>
          )}
        </div>
      </div>

      {/* Featured Pokémon */}
      {featuredNames.length > 0 && (
        <div className="mt-2 mb-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
            Featured
          </p>
          <p className="text-sm text-white/85">
            {featuredNames.join(", ")}
          </p>
        </div>
      )}

      {/* Exclusive move — the actual reason most players show up to
          a CD.  Sourced from the LeekDuck CD page directly (the
          ScrapedDuck feed doesn't expose it).  Hidden when null,
          which covers (a) date-only placeholders and (b) the rare
          case where the parser missed it. */}
      {cd.exclusive_move && (
        <div className="mt-2 mb-2 rounded-md bg-white/[0.04] border border-pink-500/20 p-2">
          <p className="text-[10px] uppercase tracking-widest text-pink-300/80 mb-1">
            Exclusive Move
            {cd.exclusive_move.move_category && (
              <span className="text-white/35 normal-case tracking-normal ml-1.5">
                · {cd.exclusive_move.move_category === "charged" ? "Charged Attack" : "Fast Attack"}
              </span>
            )}
          </p>
          <p className="text-sm font-medium text-white flex items-baseline gap-1.5 flex-wrap">
            <span>{cd.exclusive_move.move_name}</span>
            <span className={`text-[11px] font-medium ${
              TYPE_TEXT_COLORS[cd.exclusive_move.move_type.toLowerCase()] ?? "text-white/60"
            }`}>
              {cd.exclusive_move.move_type}-type
            </span>
          </p>
          {/* Power lines from LeekDuck: PvP power (+ optional buff/debuff)
              and PvE (gyms/raids) power.  Both nullable — old CDs
              ingested before 2026-05-31 won't carry these, and some
              moves legitimately publish only one of the two.  Render
              whichever is present. */}
          {(cd.exclusive_move.pvp_power != null || cd.exclusive_move.pve_power != null) && (
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
              {cd.exclusive_move.pvp_power != null && (
                <p className="text-white/70">
                  <span className="text-white/45">PvP:</span>{" "}
                  <span className="font-medium text-white">{cd.exclusive_move.pvp_power}</span> power
                  {cd.exclusive_move.pvp_buff && (
                    <span className="text-white/55"> · {cd.exclusive_move.pvp_buff}</span>
                  )}
                </p>
              )}
              {cd.exclusive_move.pve_power != null && (
                <p className="text-white/70">
                  <span className="text-white/45">Raids/Gyms:</span>{" "}
                  <span className="font-medium text-white">{cd.exclusive_move.pve_power}</span> power
                </p>
              )}
            </div>
          )}
          {cd.exclusive_move.learn_method && (
            <p className="text-[11px] text-white/55 mt-1 leading-relaxed">
              {cd.exclusive_move.learn_method}
            </p>
          )}
        </div>
      )}

      {/* Shinies */}
      {shinyNames.length > 0 && (
        <div className="mt-2 mb-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1 flex items-center gap-1">
            <ShinySparkle />
            <span>Shiny line</span>
          </p>
          <p className="text-sm text-white/70">
            {shinyNames.join(" → ")}
          </p>
        </div>
      )}

      {/* Bonuses */}
      {cd.bonuses && cd.bonuses.length > 0 && (
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
            Bonuses
          </p>
          <ul className="space-y-0.5">
            {cd.bonuses.map((b, i) => (
              <li key={i} className="flex items-baseline gap-1.5 text-xs text-white/70">
                <span className="text-pink-400/70 shrink-0" aria-hidden="true">·</span>
                <span className="break-words">{b.text ?? ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Special research count (if present) */}
      {cd.special_research && cd.special_research.length > 0 && (
        <p className="text-[11px] text-white/45 mt-2 italic">
          Includes special research story ({cd.special_research.length} step
          {cd.special_research.length === 1 ? "" : "s"})
        </p>
      )}

      {/* LeekDuck link */}
      {isSafeUrl(cd.link) && (
        <div className="mt-3 pt-2 border-t border-white/[0.06]">
          <a
            href={cd.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-pink-400/70 hover:text-pink-300 transition-colors"
          >
            Full details on LeekDuck →
          </a>
        </div>
      )}
    </article>
  );
}

/**
 * Upcoming Community Days.  Mixes fully-announced CDs (featured
 * Pokémon known, full bonuses + shinies + special research) with
 * date-only placeholders (Niantic scheduled the date but hasn't
 * announced the Pokémon yet — common for CDs 2-3 months out).
 *
 * Phase 3.
 */
export default function CommunityDaysCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const summarySec = sections.find((s) => s.section === "cd_summary");
  const cdSec = sections.find((s) => s.section === "community_days");
  const cds = (cdSec?.items as CommunityDay[] | undefined) ?? [];

  if (cds.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-pink-500/25">
        <p className="text-sm text-white/55 py-2">No upcoming Community Days on file.</p>
      </CardShell>
    );
  }

  const total = (summarySec?.total as number | undefined) ?? cds.length;
  const announced = (summarySec?.announced as number | undefined) ?? cds.filter((c) => c.is_announced).length;
  const dateOnly = total - announced;

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-pink-500/25"
    >
      {/* Summary strip */}
      <p className="text-[11px] text-white/45 mb-3">
        {total} upcoming
        {announced > 0 && <> · {announced} fully announced</>}
        {dateOnly > 0 && <> · {dateOnly} date{dateOnly === 1 ? "" : "s"} pending Pokémon announcement</>}
      </p>

      {/* Per-CD blocks */}
      <div className="space-y-3">
        {cds.map((cd, i) => (
          <CommunityDayBlock key={cd.event_id ?? i} cd={cd} />
        ))}
      </div>

      {/* Attribution footer */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[11px] text-white/35">
          Event data from{" "}
          <a
            href="https://leekduck.com/events/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-400/70 hover:text-pink-300 transition-colors"
          >
            LeekDuck
          </a>{" "}
          via{" "}
          <a
            href="https://github.com/bigfoott/ScrapedDuck"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-400/70 hover:text-pink-300 transition-colors"
          >
            ScrapedDuck
          </a>
        </p>
      </div>
    </CardShell>
  );
}
